import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CryptoError, encodeLinkKey, generateLinkKey, openSecret, sealSecret, toBase64Url } from '@sss/crypto';
import Icon, { type IconName } from '../components/Brand/Icon';
import { RouteDiagram, type Way } from '../components/Send/Ways';
import { fetchCryptoManifest, type CryptoManifest, type CryptoManifestFile } from '../lib/cryptoManifest';
import { usePageTitle } from '../hooks/usePageTitle';
import ui from '../styles/ui.module.css';
import styles from './HowItWorksPage.module.css';

// Every way to send starts with the same two steps; only the last two
// depend on the way, so the page shows those once per way, beside each other.
const SHARED_STEPS: [step: 'encrypt' | 'link', icon: IconName, badge: IconName][] = [
  ['encrypt', 'laptop', 'lock'],
  ['link', 'link', 'key'],
];
const WAYS: Way[] = ['drop', 'live', 'relay'];
const WAY_STEPS = ['travel', 'after'] as const;

interface Sealed {
  key: Uint8Array;
  payload: Uint8Array;
  passphrase: string | undefined;
}

export default function HowItWorksPage() {
  const { t } = useTranslation();
  usePageTitle(t('howItWorks.title'));
  return (
    <main className={ui.page}>
      <header className={styles.intro}>
        <h1 className={styles.title}>{t('howItWorks.title')}</h1>
        <p className={styles.lead}>{t('howItWorks.lead')}</p>
      </header>

      <section className={styles.group} aria-labelledby="every-way">
        <h2 id="every-way" className={styles.groupTitle}>
          {t('howItWorks.everyWay')}
        </h2>
        <ol className={styles.sharedSteps}>
          {SHARED_STEPS.map(([step, icon, badge], i) => (
            <li key={step} className={styles.sharedStep}>
              <span className={styles.emblem} aria-hidden="true">
                <span className={styles.emblemMain}>
                  <Icon name={icon} size={26} />
                </span>
                <span className={styles.emblemBadge}>
                  <Icon name={badge} size={13} strokeWidth={2.4} />
                </span>
              </span>
              <div className={styles.step}>
                <span className={styles.stepNumber} aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className={styles.stepTitle}>{t(`howItWorks.steps.${step}.title`)}</h3>
                <p>{t(`howItWorks.steps.${step}.body`)}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className={styles.group} aria-labelledby="depends-on-way">
        <h2 id="depends-on-way" className={styles.groupTitle}>
          {t('howItWorks.dependsOnWay')}
        </h2>
        <div className={styles.ways}>
          {WAYS.map((way) => (
            <section key={way} className={styles.way} aria-labelledby={`how-${way}`}>
              <h3 id={`how-${way}`} className={styles.wayName}>
                {t(`landing.ways.${way}.eyebrow`)}
              </h3>
              <p className={styles.wayLead}>{t(`landing.ways.${way}.lead`)}</p>
              <RouteDiagram way={way} />
              <ol className={styles.waySteps} start={SHARED_STEPS.length + 1}>
                {WAY_STEPS.map((step, i) => (
                  <li key={step} className={styles.step}>
                    <span className={styles.stepNumber} aria-hidden="true">
                      {String(SHARED_STEPS.length + i + 1).padStart(2, '0')}
                    </span>
                    <h4 className={styles.stepTitle}>{t(`howItWorks.ways.${way}.${step}.title`)}</h4>
                    <p>{t(`howItWorks.ways.${way}.${step}.body`)}</p>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      </section>

      <Demo />
      <CodeSection />
    </main>
  );
}

function Demo() {
  const { t } = useTranslation();
  const [text, setText] = useState(t('howItWorks.demo.sample'));
  const [passphrase, setPassphrase] = useState('');
  const [sealed, setSealed] = useState<Sealed | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onEncrypt(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setOpened(null);
    try {
      const key = generateLinkKey();
      const pass = passphrase.length > 0 ? passphrase : undefined;
      setSealed({ key, payload: await sealSecret(text, key, { passphrase: pass }), passphrase: pass });
    } catch (err) {
      setError(err instanceof CryptoError ? t('howItWorks.demo.error', { code: err.code }) : t('common.genericError'));
    } finally {
      setBusy(false);
    }
  }

  async function onDecrypt() {
    if (!sealed) return;
    setBusy(true);
    try {
      setOpened(await openSecret(sealed.payload, sealed.key, { passphrase: sealed.passphrase }));
    } catch (err) {
      setError(err instanceof CryptoError ? t('howItWorks.demo.error', { code: err.code }) : t('common.genericError'));
    } finally {
      setBusy(false);
    }
  }

  const payloadText = sealed ? toBase64Url(sealed.payload) : '';
  return (
    <section className={styles.section} aria-labelledby="demo-title">
      <h2 id="demo-title" className={styles.sectionTitle}>
        {t('howItWorks.demo.title')}
      </h2>
      <p className={styles.sectionLead}>
        {t('howItWorks.demo.lead')} {t('howItWorks.demo.format')}
      </p>
      <div className={styles.demo}>
        <form className={`${ui.panel} ${styles.demoForm}`} onSubmit={onEncrypt}>
          <label className={ui.field}>
            {t('howItWorks.demo.textLabel')}
            <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />
          </label>
          <label className={ui.field}>
            <span>
              {t('create.form.passphraseLabel')} <span className={ui.hint}>{t('howItWorks.demo.passphraseHint')}</span>
            </span>
            <input type="text" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} autoComplete="off" />
          </label>
          <button type="submit" className={ui.button} disabled={busy}>
            {t('howItWorks.demo.encrypt')}
          </button>
        </form>

        {/* Announce that it worked, not the key and payload themselves. */}
        <p role="status" className="visually-hidden">
          {opened !== null ? t('howItWorks.demo.decryptedStatus') : sealed ? t('howItWorks.demo.encryptedStatus') : ''}
        </p>
        <div className={styles.demoOutput}>
          {error && (
            <p role="alert" className={ui.error}>
              {error}
            </p>
          )}
          {sealed ? (
            <>
              <div>
                <div className={ui.eyebrow}>{t('howItWorks.demo.keyLabel')}</div>
                <code className={styles.value}>#{encodeLinkKey(sealed.key)}</code>
                <p className={ui.hint}>{t('howItWorks.demo.keyHint')}</p>
              </div>
              <div>
                <div className={ui.eyebrow}>{t('howItWorks.demo.payloadLabel', { bytes: sealed.payload.length })}</div>
                {/* Scrolls, so it takes keyboard focus. */}
                <code className={`${styles.value} ${styles.payload}`} tabIndex={0} aria-label={t('howItWorks.demo.payloadName')}>
                  {payloadText}
                </code>
                <p className={ui.hint}>{t('howItWorks.demo.payloadHint')}</p>
              </div>
              <button type="button" className={ui.buttonSecondary} onClick={onDecrypt} disabled={busy}>
                {t('howItWorks.demo.decrypt')}
              </button>
              {opened !== null && (
                <div>
                  <div className={ui.eyebrow}>{t('howItWorks.demo.openedLabel')}</div>
                  <p className={styles.opened}>{opened}</p>
                </div>
              )}
            </>
          ) : (
            <p className={ui.hint}>{t('howItWorks.demo.empty')}</p>
          )}
        </div>
      </div>
    </section>
  );
}

function CodeSection() {
  const { t } = useTranslation();
  const [manifest, setManifest] = useState<CryptoManifest | null | 'loading'>('loading');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    const controller = new AbortController();
    fetchCryptoManifest(controller.signal)
      .then((m) => setManifest(m))
      .catch(() => {
        if (!controller.signal.aborted) setManifest(null);
      });
    return () => controller.abort();
  }, []);

  return (
    <section id="code" className={styles.section} aria-labelledby="code-title">
      <h2 id="code-title" className={styles.sectionTitle}>
        {t('howItWorks.code.title')}
      </h2>
      <div className={ui.prose}>
        <p>{t('howItWorks.code.lead')}</p>
        <p>{t('howItWorks.code.integrity')}</p>
      </div>

      {manifest === 'loading' && <p className={ui.hint}>{t('common.loading')}</p>}
      {manifest === null && <p className={ui.hint}>{t('howItWorks.code.unavailable')}</p>}
      {manifest && manifest !== 'loading' && (
        <>
          <ul className={styles.files}>
            {manifest.files.map((file) => (
              <CodeFile key={file.path} file={file} />
            ))}
          </ul>
          <h3 className={styles.verifyTitle}>{t('howItWorks.code.verifyTitle')}</h3>
          <p>{t('howItWorks.code.verifyBody')}</p>
          <pre className={styles.command} tabIndex={0} aria-label={t('howItWorks.code.verifyTitle')}>
            <code>{`curl -s ${origin}${manifest.files.find((f) => f.path.endsWith('/secret.js'))?.path ?? manifest.entry} | shasum -a 256`}</code>
          </pre>
        </>
      )}
      <p className={`${ui.prose} ${ui.hint}`}>{t('howItWorks.code.caveat')}</p>
    </section>
  );
}

function CodeFile({ file }: { file: CryptoManifestFile }) {
  const { t } = useTranslation();
  const [source, setSource] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  function onToggle(e: React.SyntheticEvent<HTMLDetailsElement>) {
    if (!e.currentTarget.open || source !== null) return;
    fetch(file.path, { cache: 'no-store' })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(String(res.status)))))
      .then(setSource)
      .catch(() => setFailed(true));
  }

  return (
    <li className={styles.file}>
      <div className={styles.fileHeader}>
        <a href={file.path} target="_blank" rel="noreferrer" className={styles.filePath}>
          {file.path}
        </a>
        <span className={ui.hint}>{t('howItWorks.code.bytes', { bytes: file.bytes })}</span>
      </div>
      <div className={styles.hash}>
        <span className={ui.eyebrow}>SHA-256</span> <code>{file.sha256}</code>
      </div>
      <details onToggle={onToggle}>
        <summary className={styles.summary}>{t('howItWorks.code.showSource')}</summary>
        {failed && <p className={ui.error}>{t('howItWorks.code.unavailable')}</p>}
        {source !== null && (
          <pre className={styles.source} tabIndex={0} aria-label={file.path}>
            <code>{source}</code>
          </pre>
        )}
      </details>
    </li>
  );
}
